/** @jsx jsx */
import { Button, Inline, jsx, Separator, Stack, Text } from '@front10/base-elements';
import { useMobileFilter, usePopupFilter } from '../../../components/lab/FlexibleFilterContainer';
import { PeopleCounter, PeopleInfo } from '../../../components/lab/PeopleCounter';
import {
  MobileBody,
  MobileFilterDialog,
  MobileFooter,
  MobileHeader
} from '../../../components/lab/MobileFilterView';
import { Listbox } from '../../../components/lab/Listbox';
import { FilterButton } from '../../../components/lab/FilterButton';
import { Dropdown } from '../../../components/lab/Dropdown';
import { count, SelectProps } from '../../../components/lab/utils';
import { usePlural, useTranslation, useIsMobileView } from '@front10/utils';
import produce from 'immer';
import nanoid from 'nanoid';
import React, { Fragment, useState } from 'react';
import { useEffectReducer } from 'use-effect-reducer';
import { PassengersProps, RoomsProps } from './bookingBusinessRules';
import { BookingComponentsConfigurations } from './bookingComponentsConfigurations';
import { PanelSingleType } from './BookingPanel';
import { evaluateRules } from '../../lab/PeopleCounter/rulesParser';

export type RoomInfo = { key: string; count: number[]; ages: number[][] };
export type RoomsInfo = { rooms: RoomInfo[]; travelClass?: string };

interface Props {
  /**
   * Value of the guest selector
   */
  defaultValue?: Partial<RoomsInfo>;
  title?: string;
  rooms: Omit<RoomsProps, 'defaultSelection'>;
  onChange?: (newValue: RoomsInfo) => void;
  travelClasses?: Omit<SelectProps, 'defaultValue'>;
  travelClassConfig?: BookingComponentsConfigurations['travelClassSelector'];
  passengersRules?: Array<string>;
  type: PanelSingleType;
  origin?: string;
  destination?: string;
  preventBottomDisplacement?: boolean;
}

export function buildRoom(
  size: number,
  defaultSelection?: { count: number[]; ages: number[][] }
): RoomInfo {
  if (defaultSelection) {
    return {
      key: nanoid(),
      ...defaultSelection
    };
  }
  let count = Array(size);
  count.fill(0);
  count[0] = 1;

  let ages = Array(size);
  ages.fill([]);

  return {
    key: nanoid(),
    count,
    ages
  };
}

export function RoomsSelector(props: Props) {
  const translate = useTranslation();
  const plural = usePlural();
  const totalRooms = props.rooms.totalRooms;
  const totalTravelers = props.rooms.totalTravelers;
  const [focusedElement, setFocusedElement] = useState(false);
  const [id] = React.useState(nanoid());
  const [savedRooms, setSavedRooms] = useState(props.defaultValue?.rooms ?? []);
  const [travelClass, setTravelClass] = useState<string>(props.defaultValue?.travelClass ?? '');

  const { rooms, dispatch, isEventValid } = useRooms(
    props.rooms,
    savedRooms,
    (value) =>
      props.onChange?.({
        rooms: value,
        travelClass: travelClass
      }),
    props.passengersRules
  );
  const isMobile = useIsMobileView();
  const dialog = useMobileFilter({
    onDismiss: () => {
      dispatch({ type: 'SET_ROOMS', value: savedRooms });
    },
    onSave: () => {
      setSavedRooms(rooms);
    }
  });

  const popup = usePopupFilter();
  const showTravelClass =
    props.type.includes('flight') && (props.travelClassConfig?.enabled ?? true);

  const NO_VALUE = -1;
  const SHOW_TRUE_RULE = 0;
  const NO_SHOW_NO_TRUE_RULE = 1;

  function showOption(rule: any, origin: any, destination: any) {
    let modeRule = NO_VALUE;
    const typeRule1 = rule[0];
    const typeRule2 = rule[1];
    const show = rule[2];

    if (typeRule1.length !== 0) {
      let typeRoute = typeRule1[0];
      switch (typeRoute) {
        case 'origin': {
          let countrys = typeRule1[1];
          for (let k = 0; k < countrys.length; k++) {
            if (origin === countrys[k]) {
              modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
              break;
            }
          }
          if (modeRule === NO_VALUE) {
            let countrys = typeRule2[1];
            if (countrys !== undefined) {
              for (let k = 0; k < countrys.length; k++) {
                if (destination === countrys[k]) {
                  modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
                  break;
                }
              }
            }
          }
          if (modeRule === NO_VALUE) {
            modeRule = show ? NO_SHOW_NO_TRUE_RULE : SHOW_TRUE_RULE;
          }
          break;
        }
        case 'destination': {
          let countrys = typeRule1[1];
          for (let k = 0; k < countrys.length; k++) {
            if (destination === countrys[k]) {
              modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
              break;
            }
          }
          if (modeRule === NO_VALUE) {
            let countrys = typeRule2[1];
            if (countrys !== undefined) {
              for (let k = 0; k < countrys.length; k++) {
                if (origin === countrys[k]) {
                  modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
                  break;
                }
              }
            }
          }
          if (modeRule === NO_VALUE) {
            modeRule = show ? NO_SHOW_NO_TRUE_RULE : SHOW_TRUE_RULE;
          }
          break;
        }
      }
    } else if (typeRule2.length !== 0) {
      let typeRoute = typeRule2[0];

      switch (typeRoute) {
        case 'origin': {
          let countrys = typeRule2[1];
          for (let k = 0; k < countrys.length; k++) {
            if (origin === countrys[k]) {
              modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
              break;
            }
          }
          if (modeRule === NO_VALUE) {
            modeRule = show ? NO_SHOW_NO_TRUE_RULE : SHOW_TRUE_RULE;
          }
          break;
        }
        case 'destination': {
          let countrys = typeRule2[1];
          for (let k = 0; k < countrys.length; k++) {
            if (destination === countrys[k]) {
              modeRule = show ? SHOW_TRUE_RULE : NO_SHOW_NO_TRUE_RULE;
              break;
            }
          }
          if (modeRule === NO_VALUE) {
            modeRule = show ? NO_SHOW_NO_TRUE_RULE : SHOW_TRUE_RULE;
          }
          break;
        }
      }
    }
    return modeRule;
  }

  function applyBusinessRules(
    origin: string | undefined,
    destination: string | undefined,
    options: any | undefined
  ) {
    let newOptions = [];
    let rules;
    let rule;
    let element;
    let modeRule = NO_VALUE;
    for (let i = 0; i < options.length; i++) {
      element = options[i];
      if (element.rules !== undefined && element.rules.length !== 0) {
        rules = element.rules;
        for (let j = 0; j < rules.length; j++) {
          rule = rules[j];
          modeRule = showOption(rule, origin, destination);
        }
        if (modeRule === SHOW_TRUE_RULE) {
          newOptions.push(element);
        }
        modeRule = NO_VALUE;
      } else {
        newOptions.push(element);
      }
    }
    return newOptions;
  }

  const roomsCount = rooms.length;
  const guestsCount = count(rooms.map((room) => count(room.count)));

  const guestsLabel = plural({
    count: guestsCount,
    variables: {
      guestsCount
    },
    key: 'cmp-booking-guests-label',
    defaultValue: '{guestsCount} guests'
  });

  const roomsLabel = plural({
    count: roomsCount,
    variables: {
      roomsCount
    },
    key: 'cmp-booking-rooms-label',
    defaultValue: '{roomsCount} rooms'
  });

  const filterButton = (
    <FilterButton
      value={translate('cmp-booking-rooms-info-label', '{guestsLabel}, {roomsLabel}', {
        guestsLabel,
        roomsLabel
      })}
      onClick={isMobile ? dialog.open : popup.toggle}
      ref={popup.filterRef}
      active={true}
    />
  );
  const main = (
    <Fragment>
      {showTravelClass ? (
        <div sx={{ pl: 'dialogMargin', pr: 'dialogMargin' }}>
          <div id={`screenReaderMessage-${id}`} hidden>
            {translate(`cmp-booking-selected-travel-class-message`, `Selected`)}
          </div>
          <Listbox
            ariaLabelledBy={`screenReaderMessage-${id}`}
            labelNotSelectedItem={translate('cmp-travel-class-label-not-selected-item', 'Elements')}
            label={translate('cmp-travel-class-label', 'Travel Class')}
            layout="standard"
            focusWhenIsShow={false}
            options={applyBusinessRules(
              props.origin,
              props.destination,
              props.travelClasses?.options
            )}
            defaultValue={travelClass}
            onChange={(newValue) => {
              setTravelClass(newValue);
              props.onChange?.({
                rooms: rooms,
                travelClass: newValue
              });
            }}
          />
        </div>
      ) : null}
      {rooms.map((room, index) => {
        return (
          <div
            key={room.key}
            role="group"
            aria-label={`${translate('cmp-booking-room-label', 'Room')} ${index + 1}`}
          >
            <Stack gap={4} pl="dialogMargin" pr="dialogMargin">
              <Inline align="space-between">
                <Text variant="text.fontSize4" color="text">
                  {translate('cmp-booking-room-label', 'Room')}
                  {` ${index + 1}`}
                </Text>
                {index !== 0 && (
                  <Button
                    type="button"
                    variant="links.fontSize5"
                    sx={{
                      bg: 'transparent',
                      textDecoration: 'underline',
                      p: '0'
                    }}
                    onClick={() => {
                      dispatch({ type: 'REMOVE_ROOM', roomIndex: index });
                    }}
                  >
                    {translate('cmp-booking-remove-room-button', 'Remove')}
                  </Button>
                )}
              </Inline>
              <Room
                origin={props.origin}
                destination={props.destination}
                roomIndex={index}
                options={props.rooms.options}
                dispatch={dispatch}
                value={rooms[index]}
                isEventValid={isEventValid}
                totalCount={guestsCount}
                preventBottomDisplacement={props?.preventBottomDisplacement}
              />
            </Stack>
            {index < rooms.length - 1 && <Separator />}
          </div>
        );
      })}
    </Fragment>
  );

  const footer = (
    <Inline variant="layout.popover.footer" gap={2} align="end" pb="11px">
      {rooms.length < totalRooms && guestsCount < totalTravelers ? (
        <Button
          type="button"
          variant="link"
          onClick={() => {
            dispatch({ type: 'ADD_ROOM' });
          }}
        >
          {translate('cmp-booking-add-room-button', 'Add room')}
        </Button>
      ) : null}
      <Button
        type="button"
        variant="link"
        onFocus={() => {
          setFocusedElement(false);
        }}
        onClick={isMobile ? dialog.save : popup.toggle}
        value="Done"
      >
        {translate('cmp-booking-done-room-button', 'Done')}
      </Button>
    </Inline>
  );

  if (isMobile) {
    return (
      <React.Fragment>
        {filterButton}
        {dialog.isOpen && (
          <MobileFilterDialog label={props.title || 'Select rooms'}>
            <MobileHeader title={props.title} onBackButtonPress={dialog.dismiss} />
            <MobileBody>
              <Stack
                gap={4}
                pt="30px"
                pb="15px"
                sx={{ variant: 'layout.roomSelector.mobileContainer' }}
              >
                {main}
              </Stack>
            </MobileBody>
            <MobileFooter align="end">{footer}</MobileFooter>
          </MobileFilterDialog>
        )}
      </React.Fragment>
    );
  }
  return (
    <Dropdown
      variant="layout.popover"
      ref={popup.ref}
      targetRef={popup.filterRef}
      toggleElement={filterButton}
      onClose={() => {
        dialog.save();
        if (!focusedElement) {
          popup.filterRef.current.focus();
          setFocusedElement(true);
        }
      }}
    >
      <Stack gap={4} pt="30px" sx={{ variant: 'layout.roomSelector.dropdown' }}>
        {main}
        {footer}
      </Stack>
    </Dropdown>
  );
}

interface RoomProps {
  options: PassengersProps['options'];
  roomIndex: number;
  value: PeopleInfo;
  dispatch: ReturnType<typeof useRooms>['dispatch'];
  origin?: string;
  destination?: string;
  isEventValid: ReturnType<typeof useRooms>['isEventValid'];
  totalCount: number;
  preventBottomDisplacement?: boolean;
}

function Room(props: RoomProps) {
  const dispatch = props.dispatch;
  const roomIndex = props.roomIndex;
  const passengerDispatch = React.useCallback(
    (args: any) => {
      dispatch({ ...args, roomIndex: roomIndex });
    },
    [dispatch, roomIndex]
  );
  return (
    <PeopleCounter
      origin={props.origin}
      destination={props.destination}
      options={props.options}
      layout="inline"
      dispatch={passengerDispatch}
      value={props.value}
      isEventValid={(event, state) => props.isEventValid(event, state, props.totalCount)}
      preventBottomDisplacement={props?.preventBottomDisplacement}
    />
  );
}

export function useRooms(
  roomProps: Omit<RoomsProps, 'defaultSelection'>,
  defaultState?: RoomInfo[],
  onChange?: (value: RoomInfo[]) => void,
  rules: Array<string> = []
) {
  const maxCount = roomProps.options.map((v) => v.maxCount);
  const minCount = roomProps.options.map((v) => v.minCount ?? 0);
  const guestsTypesCount = roomProps.options.length;
  const initialState = defaultState ?? [buildRoom(guestsTypesCount)];
  const totalTravelers = roomProps.totalTravelers ?? 1000;
  const totalTravelersPerRoom = roomProps.totalTravelersPerRoom ?? 1000;
  const excludeChildren = roomProps.totalTravelersPerRoomExcludeInfants;

  const isEventValid = (event: any, room: Omit<RoomInfo, 'key'>, totalCount: number) => {
    const { valid } = evaluator(room, event.type, event.index);
    switch (event.type) {
      case 'ADD': {
        const roomCount = count(room.count.filter((item, index) => index !== infantIndex)); // : count(room.count);
        const passengerIndex = event.index;
        //Bypass when children and exclude flag is true
        const excludeCondition =
          excludeChildren && passengerIndex === infantIndex
            ? true
            : roomCount < totalTravelersPerRoom;
        //To allow add children when total of persons is complete
        const canAddChild =
          excludeChildren && infantIndex === event.index ? true : totalCount < totalTravelers;

        return (
          valid &&
          excludeCondition &&
          canAddChild &&
          (maxCount[passengerIndex] === undefined ||
            room.count[passengerIndex] < (maxCount[passengerIndex] as number))
        );
      }
      case 'REMOVE': {
        const passengerIndex = event.index;
        return room.count[passengerIndex] > minCount[passengerIndex];
      }
      default: {
        return true;
      }
    }
  };

  const evaluator = evaluateRules(rules, roomProps);
  //the value to get Infant index is hardcoded because not should change in a future
  const infantIndex = roomProps?.totalTravelersPerRoomExcludeInfants
    ? roomProps.options.findIndex((item) => item.name === 'age3')
    : -1;

  const [rooms, dispatch] = useEffectReducer((state, event, exec) => {
    const newState = produce(state, (draft) => {
      const totalCount = count(
        state.map((room) => {
          return count(room.count.filter((itemCount, index) => index !== infantIndex));
        })
      );
      const { ruleActions } = evaluator(draft[event.roomIndex], event.type, event.index);
      switch (event.type) {
        case 'ADD': {
          const roomIndex = event.roomIndex;
          const room = draft[roomIndex];
          const passengerIndex = event.index;
          if (isEventValid(event, room, totalCount)) {
            room.count[passengerIndex] += 1;
            room.ages[passengerIndex].push(roomProps.options[passengerIndex].minAge);
          }
          break;
        }
        case 'REMOVE': {
          const roomIndex = event.roomIndex;
          const room = draft[roomIndex];
          const passengerIndex = event.index;
          if (isEventValid(event, room, totalCount)) {
            room.count[passengerIndex] -= 1;
            room.ages[passengerIndex].pop();
            ruleActions(room);
          }
          break;
        }
        case 'ADD_ROOM': {
          if (totalCount < totalTravelers) {
            draft.push(buildRoom(guestsTypesCount));
          }
          break;
        }
        case 'REMOVE_ROOM': {
          const roomIndex = event.roomIndex;
          draft.splice(roomIndex, 1);
          break;
        }
        case 'SET_ROOMS': {
          return event.value;
        }
        case 'AGE_CHANGE': {
          const roomIndex = event.roomIndex;
          const room = draft[roomIndex];
          const typeIndex = event.typeIndex;
          const instanceIndex = event.instanceIndex;
          room.ages[typeIndex][instanceIndex] = event.value;
          break;
        }
        case 'VALUE_SET': {
          const roomIndex = event.roomIndex;
          const room = draft[roomIndex];
          room.ages = event.value.ages;
          room.count = event.value.count;
        }
      }
    });
    exec(() => onChange?.(newState));
    return newState;
  }, initialState);

  return { rooms, dispatch, isEventValid };
}
